import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { useAuthStore } from "../store";

type Category = { id: string; name: string; description: string; createdAt: string; updatedAt: string };
type FormState = { name: string; description: string };

const emptyForm: FormState = { name: "", description: "" };

export function CategoryPage() {
  const token = useAuthStore((state) => state.token);
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const categories = useQuery({ queryKey: ["categories"], queryFn: () => api<Category[]>("/api/v1/categories", {}, token || undefined) });

  const save = useMutation({
    mutationFn: (payload: FormState) => editingId
      ? api<Category>(`/api/v1/categories/${editingId}`, { method: "PUT", body: JSON.stringify(payload) }, token || undefined)
      : api<Category>("/api/v1/categories", { method: "POST", body: JSON.stringify(payload) }, token || undefined),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      setError("");
    },
    onError: (requestError: Error) => setError(requestError.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api<{ deleted: boolean }>(`/api/v1/categories/${id}`, { method: "DELETE" }, token || undefined),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["categories"] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (requestError: Error) => setError(requestError.message),
  });

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError("");
    setOpen(true);
  };

  const startEdit = (category: Category) => {
    setEditingId(category.id);
    setForm({ name: category.name, description: category.description || "" });
    setError("");
    setOpen(true);
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError("Category name is required");
      return;
    }
    save.mutate({ name: form.name.trim(), description: form.description.trim() });
  };

  return <section><div className="page-heading"><div><p className="eyebrow">04 / STOCK</p><h2>Categories</h2><p className="subhead">Keep the product catalog organized around the groups your team uses every day.</p></div><button className="primary-button" onClick={startCreate}>Add Category</button></div>
    {open && <div className="panel form-panel"><div className="table-head"><h3>{editingId ? "Edit category" : "New category"}</h3><button className="ghost-button" type="button" onClick={() => setOpen(false)}>Cancel</button></div><form className="crud-form" onSubmit={submit}><label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required /></label><label>Description<textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>{error && <div className="notice">{error}</div>}<button className="primary-button" type="submit" disabled={save.isPending}>{save.isPending ? "Saving…" : editingId ? "Save changes" : "Create category"}</button></form></div>}
    {categories.isError && <div className="notice">{(categories.error as Error).message}</div>}
    <div className="panel table-panel"><div className="table-head"><h3>Catalog groups</h3><span className="tag">{categories.data?.length || 0} RECORDS</span></div>{categories.isLoading ? <div className="module-empty"><p>Loading categories…</p></div> : categories.data?.length ? <div className="record-list">{categories.data.map((category) => <div className="record-row" key={category.id}><div><strong>{category.name}</strong><span>{category.description || "No description"}</span></div><div className="record-actions"><button className="ghost-button" onClick={() => startEdit(category)}>Edit</button><button className="ghost-button danger" onClick={() => { if (window.confirm(`Delete ${category.name}? Categories assigned to products cannot be deleted.`)) remove.mutate(category.id); }}>Delete</button></div></div>)}</div> : <div className="module-empty"><p>No categories have been added yet.</p><button className="ghost-button" onClick={startCreate}>Create first category</button></div>}</div>
  </section>;
}
